import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import Sidebar from '../components/Sidebar';
import '../styles/theme.css';
import './AutoApplySettings.css';

function AutoApplySettings() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    is_enabled: false,
    min_match_score: 60,
    preferred_locations: '',
    excluded_companies: '',
    preferred_job_types: '',
  });
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [message, setMessage] = useState({ text: '', type: '' });
  const userTier = localStorage.getItem('user_tier') || 'free';

  useEffect(() => { fetchSettings(); fetchStats(); fetchHistory(); }, []);

  const fetchSettings = async () => {
    try { const r = await api.get('/auto-apply/settings'); setSettings(r.data); }
    catch {} finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try { const r = await api.get('/auto-apply/stats'); setStats(r.data); } catch {}
  };

  const fetchHistory = async () => {
    try { const r = await api.get('/auto-apply/history'); setHistory(r.data.history || []); } catch {}
  };

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post('/auto-apply/settings', settings); showMsg('Settings saved.'); fetchStats(); }
    catch (err) { showMsg('Error: ' + (err.response?.data?.detail || err.message), 'error'); }
    finally { setSaving(false); }
  };

  const handleToggle = async () => {
    try {
      const r = await api.post('/auto-apply/toggle');
      setSettings(p => ({ ...p, is_enabled: r.data.is_enabled }));
      showMsg(r.data.message); fetchStats();
    } catch (err) { showMsg(err.response?.data?.detail || err.message, 'error'); }
  };

  const handleRunNow = async () => {
    try { const r = await api.post('/auto-apply/run'); showMsg(r.data.message); fetchStats(); fetchHistory(); }
    catch (err) { showMsg(err.response?.data?.detail || err.message, 'error'); }
  };

  const tierLabel = (t) => ({ free: 'Free', pro: 'Pro', premium: 'Premium' }[t] || t);

  if (loading) return (
    <div className="portal-layout"><Sidebar />
      <main className="portal-main"><div className="portal-content"><div className="empty-state">
        <i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i>
        <p>Loading settings...</p>
      </div></div></main>
    </div>
  );

  const isFree = !stats || stats.daily_limit === 1;
  const streakDone = isFree && stats && stats.max_consecutive_days && stats.consecutive_days_used >= stats.max_consecutive_days;

  return (
    <div className="portal-layout">
      <Sidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">Auto-Apply</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme}><i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i></button>
          </div>
        </div>

        <div className="portal-content">
          <div className="page-header">
            <h1>Auto-Apply Settings</h1>
            <p>Automate job applications based on your preferences and match score</p>
          </div>

          {message.text && (
            <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>
              <i className={`fas ${message.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'}`}></i>
              {message.text}
            </div>
          )}

          {/* Tier limits banner */}
          {stats && (
            <div className="card" style={{ marginBottom: '1.5rem', background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div>
                  <span className="badge badge-blue" style={{ marginRight: '0.5rem' }}>{tierLabel(userTier)} Plan</span>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    {stats.daily_limit} application{stats.daily_limit !== 1 ? 's' : ''}/day
                    {stats.monthly_limit ? ` · ${stats.monthly_limit}/month` : ' · Unlimited/month'}
                    {stats.max_consecutive_days ? ` · ${stats.max_consecutive_days} consecutive days` : ''}
                  </span>
                </div>
                {isFree && (
                  <button className="btn btn-primary btn-sm" onClick={() => navigate('/pricing')}>
                    <i className="fas fa-arrow-up"></i> Upgrade for more
                  </button>
                )}
              </div>

              {/* Free streak progress */}
              {stats.max_consecutive_days && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>
                    <span>Consecutive days used</span>
                    <span>{stats.consecutive_days_used} / {stats.max_consecutive_days} days</span>
                  </div>
                  <div style={{ background: 'var(--border)', borderRadius: '99px', height: '6px' }}>
                    <div style={{
                      width: `${Math.min(100, (stats.consecutive_days_used / stats.max_consecutive_days) * 100)}%`,
                      background: streakDone ? 'var(--danger)' : 'var(--primary)',
                      borderRadius: '99px', height: '6px', transition: 'width 0.3s'
                    }} />
                  </div>
                  {streakDone && (
                    <p style={{ fontSize: '0.8rem', color: 'var(--danger)', marginTop: '0.4rem' }}>
                      <i className="fas fa-exclamation-circle"></i> Free streak limit reached. Upgrade to continue auto-applying.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Stats row */}
          {stats && (
            <div className="stats-row" style={{ marginBottom: '1.5rem' }}>
              {[
                { icon: 'fa-paper-plane',    color: 'blue',   value: stats.total_auto_applied,          label: 'Total Applied' },
                { icon: 'fa-calendar-day',   color: 'orange', value: `${stats.applications_today} / ${stats.daily_limit}`, label: 'Today' },
                { icon: 'fa-calendar-alt',   color: 'purple', value: stats.monthly_limit ? `${stats.applications_this_month} / ${stats.monthly_limit}` : stats.applications_this_month, label: 'This Month' },
                { icon: settings.is_enabled ? 'fa-check-circle' : 'fa-times-circle',
                  color: settings.is_enabled ? 'green' : 'orange',
                  value: settings.is_enabled ? 'ON' : 'OFF', label: 'Status' },
              ].map((s, i) => (
                <div key={i} className="stat-card">
                  <div className={`stat-icon-wrap ${s.color}`}><i className={`fas ${s.icon}`}></i></div>
                  <div className="stat-info">
                    <div className="stat-value">{s.value}</div>
                    <div className="stat-label">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="aa-grid">
            {/* Toggle card */}
            <div className="card">
              <div className="card-header">
                <span className="card-title"><i className="fas fa-bolt" style={{ color: 'var(--warning)', marginRight: '0.5rem' }}></i>Auto-Apply Status</span>
                <span className={`badge ${settings.is_enabled ? 'badge-green' : 'badge-gray'}`}>
                  {settings.is_enabled ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1.25rem' }}>
                {settings.is_enabled
                  ? 'Auto-apply is active. Matching jobs will be applied to automatically within your daily limit.'
                  : 'Auto-apply is off. Enable it to start applying to matching jobs automatically.'}
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  className={`btn ${settings.is_enabled ? 'btn-danger' : 'btn-primary'}`}
                  onClick={handleToggle}
                  disabled={streakDone}
                >
                  <i className={`fas ${settings.is_enabled ? 'fa-toggle-off' : 'fa-toggle-on'}`}></i>
                  {settings.is_enabled ? 'Disable' : 'Enable'} Auto-Apply
                </button>
                {settings.is_enabled && stats?.applications_remaining_today > 0 && !streakDone && (
                  <button className="btn btn-secondary" onClick={handleRunNow}>
                    <i className="fas fa-play"></i> Run Now
                  </button>
                )}
              </div>
            </div>

            {/* Settings form */}
            <form onSubmit={handleSave}>
              <div className="card" style={{ marginBottom: '1.25rem' }}>
                <div className="card-header">
                  <span className="card-title"><i className="fas fa-sliders-h" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Application Rules</span>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Minimum Match Score</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <input type="range" name="min_match_score" min="0" max="100" step="5"
                      value={settings.min_match_score} onChange={handleChange}
                      style={{ flex: 1, accentColor: 'var(--primary)' }} />
                    <span className={`badge ${settings.min_match_score >= 65 ? 'badge-green' : settings.min_match_score >= 50 ? 'badge-blue' : 'badge-orange'}`}
                      style={{ minWidth: '48px', justifyContent: 'center' }}>
                      {settings.min_match_score}%
                    </span>
                  </div>

                  {/* Smart recommendation */}
                  {settings.min_match_score < 65 && (
                    <div style={{
                      marginTop: '0.75rem',
                      padding: '0.75rem 1rem',
                      background: 'rgba(245, 158, 11, 0.08)',
                      border: '1px solid rgba(245, 158, 11, 0.25)',
                      borderRadius: 'var(--radius-md)',
                      display: 'flex', alignItems: 'flex-start', gap: '0.6rem'
                    }}>
                      <i className="fas fa-lightbulb" style={{ color: '#f59e0b', marginTop: '2px', flexShrink: 0 }}></i>
                      <div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#f59e0b', marginBottom: '0.2rem' }}>
                          Recommendation: Set threshold to 65% or higher
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                          A lower threshold wastes your auto-apply tokens on weak matches. Jobs above 65% are significantly more likely to result in a response. You have <strong>{stats?.applications_remaining_today ?? '—'}</strong> application{stats?.applications_remaining_today !== 1 ? 's' : ''} left today — make them count.
                        </div>
                        <button
                          type="button"
                          style={{
                            marginTop: '0.5rem', padding: '0.3rem 0.75rem',
                            background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)',
                            borderRadius: '6px', color: '#f59e0b', fontSize: '0.78rem',
                            fontWeight: 600, cursor: 'pointer'
                          }}
                          onClick={() => setSettings(p => ({ ...p, min_match_score: 65 }))}
                        >
                          Set to 65% <i className="fas fa-arrow-right" style={{ marginLeft: '4px' }}></i>
                        </button>
                      </div>
                    </div>
                  )}

                  {settings.min_match_score >= 65 && settings.min_match_score < 80 && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <i className="fas fa-check-circle"></i>
                      Good threshold — you'll apply to quality matches without wasting tokens.
                    </div>
                  )}

                  {settings.min_match_score >= 80 && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <i className="fas fa-info-circle"></i>
                      High threshold — fewer applications but very strong matches only.
                    </div>
                  )}

                  <div className="form-hint" style={{ marginTop: '0.5rem' }}>Only apply to jobs with a match score above this threshold</div>
                </div>
              </div>

              <div className="card" style={{ marginBottom: '1.25rem' }}>
                <div className="card-header">
                  <span className="card-title"><i className="fas fa-filter" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Filters</span>
                </div>
                <div className="form-group">
                  <label className="form-label">Preferred Locations</label>
                  <input type="text" name="preferred_locations" className="form-control"
                    placeholder="e.g., Johannesburg, Cape Town, Remote"
                    value={settings.preferred_locations || ''} onChange={handleChange} />
                  <div className="form-hint">Comma-separated list of preferred locations</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Excluded Companies</label>
                  <input type="text" name="excluded_companies" className="form-control"
                    placeholder="e.g., Company A, Company B"
                    value={settings.excluded_companies || ''} onChange={handleChange} />
                  <div className="form-hint">Companies to skip when auto-applying</div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Preferred Job Types</label>
                  <input type="text" name="preferred_job_types" className="form-control"
                    placeholder="e.g., Full-time, Remote, Contract"
                    value={settings.preferred_job_types || ''} onChange={handleChange} />
                  <div className="form-hint">Comma-separated list of preferred employment types</div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => navigate('/dashboard')}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>
          </div>

          {/* Auto-Apply History */}
          {history.length > 0 && (
            <div className="card" style={{ marginTop: '1.5rem' }}>
              <div className="card-header">
                <span className="card-title">
                  <i className="fas fa-history" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>
                  Recent Auto-Applications
                </span>
                <span className="badge badge-blue">{history.length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {history.map((item) => (
                  <div key={item.application_id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.75rem', background: 'var(--surface-2)', borderRadius: 'var(--radius-md)',
                    flexWrap: 'wrap', gap: '0.5rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: '8px', background: 'var(--primary-light)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--primary)', fontSize: '0.9rem'
                      }}>
                        <i className="fas fa-bolt"></i>
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>{item.job_title}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{item.company}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span className="badge badge-blue">{Math.round(item.match_score)}% match</span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {item.applied_at ? new Date(item.applied_at).toLocaleDateString('en-ZA') : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}

export default AutoApplySettings;
